import { httpPost, httpPatchMerge, httpDelete } from './http'
import {
  getCompanyFromOpportunityMasterService,
  getSuppliersFromOpportunityMasterService,
} from './opportunityMasterServices'
import { getContactsForCompanyIdPreProcessed } from './companies'
import { getContactsForSupplierIdPreProcessed } from './suppliers'
import type { IOpportunityMSContact } from '~/types/opportunity/models'
import type { TEntityId, IEntitySelectItem } from '~/types/models'
import { Contact } from '~/cynqueModules/dataObjects/Contact'

const apiEntityPath = '/api/opportunitymscontacts'

/**
 * Candidate contacts for the "add contact" picker on an opportunity master service (service
 * group): the opportunity's company contacts plus the contacts of every supplier used by
 * that master service. Both the company and the suppliers are resolved from the master
 * service id (the msContact's parent), so nothing beyond that id is needed.
 */
export async function getAddableContactsForOpportunityMasterServicePreProcessed(
  masterServiceId: TEntityId
) {
  const [company, suppliersRaw] = await Promise.all([
    getCompanyFromOpportunityMasterService(masterServiceId),
    // (mis)typed as IOpportunityMasterService but returns the supplier list at runtime.
    getSuppliersFromOpportunityMasterService(masterServiceId),
  ])
  const suppliers = (suppliersRaw as unknown as IEntitySelectItem[]) ?? []
  const supplierIds = suppliers.map((s) => s.id)

  const [companyContacts, ...supplierContactLists] = await Promise.all([
    company?.id
      ? getContactsForCompanyIdPreProcessed(company.id)
      : Promise.resolve([]),
    ...supplierIds.map((id) => getContactsForSupplierIdPreProcessed(id)),
  ])
  // Re-run the generic Contact() factory so the picker's `namePlusBiz` label reads
  // "Name | company/supplier | role" (see getAddableContactsForOpportunityPreProcessed).
  return [...companyContacts, ...supplierContactLists.flat()].map((c) =>
    Contact(c)
  )
}

export async function createOpportunityMSContact(
  entity: IOpportunityMSContact
) {
  const callingMn = createOpportunityMSContact.name
  const apiPath = apiEntityPath
  return httpPost<IOpportunityMSContact>({
    callingMn,
    apiPath,
    entity,
  })
}

export async function updateOpportunityMSContact(
  entityData: Partial<IOpportunityMSContact>,
  id: TEntityId
) {
  const callingMn = updateOpportunityMSContact.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialOpportunityMSContact = Partial<IOpportunityMSContact>
  return httpPatchMerge<TPartialOpportunityMSContact>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteOpportunityMSContact(
  entityData: IOpportunityMSContact
) {
  const callingMn = deleteOpportunityMSContact.name
  const apiPath = apiEntityPath
  return httpDelete<IOpportunityMSContact>({
    callingMn,
    apiPath,
    entityData,
  })
}
